import { apiErrorMessage, useCreateCompanyClass } from "@vidario/api-client";
import { classPickerOptions, type ClassPickerOption, type CompanyClassSummary } from "@vidario/domain";
import { Combobox, useToast } from "@vidario/ui";
import { useState, type ReactElement } from "react";

export interface ClassPickerProps {
  classes: readonly CompanyClassSummary[];
  /** Called with the picked (or just created) class. */
  onPick: (classId: number, className: string) => void;
  /** The class the rows are already in; left out of the list. */
  currentClassId?: number | null;
  placeholder?: string;
  "aria-label": string;
  size?: "sm" | "md";
  disabled?: boolean;
  id?: string;
  /** False where focus arrives on its own (the next row after a pick). */
  openOnFocus?: boolean;
}

/** Combobox of company classes: „Als Sonstige markieren" first, then the
 * classes, then „Neue Klasse „…" anlegen", which creates the class and
 * picks it in one step. Picking acts at once; there is no save button. */
export function ClassPicker({
  classes,
  onPick,
  currentClassId,
  placeholder = "Klasse wählen",
  size = "sm",
  disabled,
  id,
  openOnFocus = true,
  ...aria
}: ClassPickerProps): ReactElement {
  const [query, setQuery] = useState("");
  const createClass = useCreateCompanyClass();
  const { push } = useToast();

  const options = classPickerOptions(classes, query, currentClassId);

  const pick = (option: ClassPickerOption): void => {
    setQuery("");
    if (option.kind !== "create") {
      const picked = classes.find((c) => c.id === option.classId);
      onPick(option.classId, picked?.name ?? option.label);
      return;
    }
    createClass.mutate(option.name, {
      onSuccess: (created) => onPick(created.id, created.name),
      onError: (err) => push({ kind: "error", title: "Klasse nicht angelegt", message: apiErrorMessage(err) }),
    });
  };

  return (
    <Combobox<ClassPickerOption>
      items={options}
      itemKey={(o) => o.key}
      itemToString={(o) => o.label}
      renderItem={(o) =>
        o.kind === "create" ? (
          <span className="font-medium text-primary-700">{o.label}</span>
        ) : o.kind === "fallback" ? (
          <span className="block border-b border-border-default pb-2 text-text-secondary">{o.label}</span>
        ) : (
          o.label
        )
      }
      inputValue={query}
      onInputValueChange={setQuery}
      onSelect={pick}
      clearOnSelect
      placeholder={placeholder}
      size={size}
      openOnFocus={openOnFocus}
      disabled={disabled || createClass.isPending}
      emptyMessage="Keine Klasse gefunden"
      aria-label={aria["aria-label"]}
      {...(id !== undefined && { id })}
    />
  );
}
